import { AttributesList } from '@app/components/various/AttributesList';
import { getCountries, getProductDetailsAttributes } from '@app/ducks';
import { applyCountryNameToAttribute, getCustomAttributesWithValue } from '@app/logic/products';
import { cn } from '@app/utils/cn';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Product } from '@typings';
import { prop, uniqBy } from 'ramda';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

interface Props {
  product: Product.Standard;
  className?: string;
  isDisabled?: boolean;
  isCompact?: boolean;
  shouldShowRrp?: boolean;
  isCheckout?: boolean;
}

export const ProductAttributes = ({ product, className, isDisabled, isCompact, shouldShowRrp = false, isCheckout = false }: Props) => {
  const { t } = useTranslation(['common']);
  const attributes = useSelector(getProductDetailsAttributes);
  const countries = useSelector(getCountries);
  const hasRrp = isDefined(product.rrp) && !isEmpty(product.rrp);

  const attributesWithValue = React.useMemo(() => {
    const allAttributes = uniqBy(prop('key'), [
      ...(shouldShowRrp && hasRrp ? [{ key: 'rrp', name: t('common:rrp'), value: product.rrp }] : []),
      ...applyCountryNameToAttribute(getCustomAttributesWithValue(attributes, product), countries),
    ]);

    if (isCheckout) {
      return allAttributes.filter(attr => attr.key === 'rrp' || attr.key === 'sku');
    }

    return allAttributes;
  }, [attributes, countries, hasRrp, product, shouldShowRrp, t, isCheckout]);

  if (isEmpty(attributesWithValue)) {
    return null;
  }

  return (
    <AttributesList
      attributes={attributesWithValue}
      containerClassName={cn(className, { 'opacity-50': isDisabled })}
      isCompact={isCompact}
    />
  );
};
